import { useCallback, useEffect, useState } from 'react';
import {
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import {
  BaseTransaction,
  ResponseError,
  responseErrorsResolver,
  apiAbortController,
  PermissionEnum,
  BaseellaStatus,
} from '@ui/src';
import { useLazyGetStatusesTransactionByIdQuery } from '../../../store/statuses/services';
import {
  useCalculateTransactionRatesByIdMutation,
  useCalculateTransactionRatesMutation,
  useCreateExchangeTransactionMutation,
  useLazyGetExchangeListQuery,
  useLazyGetExchangeTransactionByIdQuery,
  useUpdateExchangeTransactionMutation,
  useLazyGetSyncedTransactionsQuery,
  useLazyGetExchangeIntegrationLogsQuery,
} from '../store/services';
import StatusesUrl from '../../../types/statuses-url';
import {
  CalculateTransactionRateByIdFormValues,
  CalculateTransactionRateFormValues,
  CalculateTransactionRateResult,
} from '../types';
import useRetry from '../../outgoing/helpers/use-retry';

const useRequests = ({
  setErrorMessage,
  setStatusChangeError,
  getValues,
  setDefaultCalculateResult,
  defaultCalculateResult,
  calculateResult,
  calculatedType,
  transactionId,
  setError,
  transaction,
  setCalculatedType,
  setValue,
  setCalculatedBodyValues,
  activePermissions,
  fields,
  exchangeType,
  prevExchangeActualRate,
  calculatedBodyValues,
  prevExchangeMarginPercent,
  prevExchangeDateOfRate,
  prevExchangeType,
  prevSellSum,
  prevBuySum,
  prevCurrencyFrom,
  prevCurrencyTo,
  rateSource,
  prevRateSource,
  clearErrors,
  prevSender,
  prevInversedRate,
  inverseRateChanged,
  setInverseRateChanged,
}: {
  setErrorMessage: (data: string) => void;
  setStatusChangeError: (data: string) => void;
  getValues: UseFormGetValues<any>;
  clearErrors: UseFormClearErrors<any>;
  setDefaultCalculateResult: (data: {
    exchangeActualRate?: number;
    exchangeMarginPercent?: number;
    exchangeDateOfRate?: string;
    inverseActualRate?: number;
  }) => void;
  calculateResult?: CalculateTransactionRateResult;
  calculatedType?: 'rate' | 'margin' | 'date' | 'inversed-rate';
  setCalculatedType: (
    data: 'rate' | 'margin' | 'date' | 'inversed-rate',
  ) => void;
  transactionId?: string;
  setError: UseFormSetError<any>;
  transaction?: BaseTransaction;
  defaultCalculateResult?: {
    exchangeActualRate?: number;
    exchangeMarginPercent?: number;
    exchangeDateOfRate?: string;
  };
  setValue: UseFormSetValue<any>;
  setCalculatedBodyValues: (data: any) => void;
  activePermissions?: PermissionEnum[];
  fields: any;
  exchangeType?: string;
  prevExchangeActualRate?: string;
  calculatedBodyValues: any;
  prevExchangeMarginPercent?: string;
  prevExchangeDateOfRate?: string;
  prevExchangeType?: string;
  prevSellSum?: string;
  prevBuySum?: string;
  prevCurrencyFrom?: string;
  prevCurrencyTo?: string;
  rateSource?: string;
  prevRateSource?: string;
  prevSender?: string;
  prevInversedRate?: string;
  setInverseRateChanged: (data: boolean) => void;
  inverseRateChanged?: boolean;
}) => {
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [calcError, setCalcError] = useState('');
  const [calculateOnSwapEnabled, setCalculateOnSwapEnabled] = useState(false);
  const [exchangeDateWithTime, setExchangeDateWithTime] = useState(false);
  const [postingDateWithTime, setPostingDateWithTime] = useState(false);
  const [transferDateWithTime, setTransferDateWithTime] = useState(false);

  const [getExchangeTransactionById, { isLoading, isUninitialized }] =
    useLazyGetExchangeTransactionByIdQuery();
  const [
    getStatusesTransactionById,
    {
      isLoading: isLoadingStatuses,
      isFetching: isFetchingStatuses,
      isUninitialized: isUninitializedStatuses,
    },
  ] = useLazyGetStatusesTransactionByIdQuery();
  const [create, { isSuccess: createSuccess, isLoading: createInProgress }] =
    useCreateExchangeTransactionMutation();
  const [update, { isLoading: editInProgress, isError: editIsError }] =
    useUpdateExchangeTransactionMutation();
  const [calculate, { isLoading: calculateInProgress }] =
    useCalculateTransactionRatesMutation();
  const [calculateById, { isLoading: calculateByIdInProgress }] =
    useCalculateTransactionRatesByIdMutation();
  const [getTransactionsList] = useLazyGetExchangeListQuery();
  const [
    getSyncedTransactions,
    {
      isLoading: syncedTransactionsIsLoading,
      isFetching: syncedTransactionsIsFetching,
    },
  ] = useLazyGetSyncedTransactionsQuery();
  const [
    getIntegrationLogs,
    {
      isLoading: integrationLogsIsLoading,
      isFetching: integrationLogsIsFetching,
    },
  ] = useLazyGetExchangeIntegrationLogsQuery();

  const onSubmit = async () => {
    setErrorMessage('');
    setStatusChangeError('');

    const values = getValues();

    const date = new Date(values.exchangeDateOfRate);

    setDefaultCalculateResult({
      exchangeActualRate: Number(values.exchangeActualRate),
      exchangeMarginPercent: Number(values.exchangeMarginPercent),
      exchangeDateOfRate: values.exchangeDateOfRate,
    });

    const body: {
      exchangeCurrencyFlexibleSum: number;
      depositDateOfDestinationAccount?: string;
      exchangeDateOfRate?: string;
      exchangeActualRate?: number;
      exchangeMarginPercent?: number;
      exchangeInverseActualRate?: number;
    } = {
      exchangeCurrencyFlexibleSum: Number(calculateResult?.calculatedAmount),
    };

    if (values.depositDateOfDestinationAccount) {
      const depositeDate = new Date(values.depositDateOfDestinationAccount);

      if (postingDateWithTime) {
        body.depositDateOfDestinationAccount = depositeDate?.toUTCString();
      } else {
        const currentDate = new Date();
        const hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();
        const seconds = currentDate.getSeconds();

        depositeDate.setHours(hours);
        depositeDate.setMinutes(minutes);
        depositeDate.setSeconds(seconds);
        body.depositDateOfDestinationAccount = depositeDate?.toISOString();
      }
    }

    if (calculatedType === 'date') {
      if (exchangeDateWithTime) {
        body.exchangeDateOfRate = date.toUTCString();
      } else {
        const currentDate = new Date();
        const hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();
        const seconds = currentDate.getSeconds();

        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(seconds);
        body.exchangeDateOfRate = date.toISOString();
      }

      body.exchangeMarginPercent = Number(calculateResult?.marginPercent);
    } else if (calculatedType === 'margin') {
      body.exchangeMarginPercent = Number(calculateResult?.marginPercent);
    } else if (calculatedType === 'rate') {
      body.exchangeActualRate = Number(calculateResult?.actualRate);
    } else if (calculatedType === 'inversed-rate') {
      body.exchangeInverseActualRate = Number(
        calculateResult?.inverseActualRate,
      );
    }

    const response = (await update({
      id: transactionId as string,
      body,
    })) as {
      data?: BaseTransaction;
      error: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else {
      getStatusesTransactionById({
        id: transactionId as string,
        url: StatusesUrl.EXCHANGE,
        baseellaStatusFrom: transaction?.latestQueuedStatuses
          ?.baseellaStatus as string,
      });
    }

    return response;
  };

  const getSyncedTransactionsAction = async (ids: string[]) => {
    const queryValue = `?id=${ids?.join('id=')}&all=true`;
    await getSyncedTransactions(queryValue);
  };

  const calculateTransactionRatesByIdAction = useCallback(
    async (
      calculateFields: CalculateTransactionRateByIdFormValues,
      type: 'rate' | 'margin' | 'date' | 'inversed-rate',
    ) => {
      setCalculatedType(type);

      apiAbortController.abortPendingRequest();
      setCalcError('');
      setErrorMessage('');

      const body: CalculateTransactionRateByIdFormValues = {
        exchangeDateOfRate:
          calculateFields?.exchangeDateOfRate ||
          transaction?.exchangeDateOfRate,
        exchangeRateSourceId: transaction?.exchangeRateSourceId,
      };

      if (type === 'rate') {
        body.exchangeActualRate = Number(calculateFields.exchangeActualRate);
      } else if (type === 'inversed-rate') {
        body.inverseActualRate = Number(calculateFields.inverseActualRate);
      } else if (type === 'margin' || type === 'date') {
        body.exchangeMarginPercent = Number(
          calculateFields.exchangeMarginPercent,
        );
      }

      setDefaultCalculateResult({
        exchangeActualRate: Number(calculateFields.exchangeActualRate),
        exchangeMarginPercent: Number(calculateFields.exchangeMarginPercent),
        exchangeDateOfRate: calculateFields.exchangeDateOfRate,
        inverseActualRate: Number(calculateFields.inverseActualRate),
      });

      const response = (await calculateById({
        id: transactionId as string,
        body,
      })) as {
        error: ResponseError | { data: { message: string } };
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCalcError,
        });
      }
    },
    [defaultCalculateResult],
  );

  const calculateTransactionRatesAction = useCallback(
    async (calculateFields: {
      exchangeDateOfRate: string;
      exchangeRateSourceId: string;
      exchangeCurrencyFromId: string;
      exchangeCurrencyToId: string;
      exchangeCurrencyFixedId: string;
      sum: string;
      senderAccountNumber: string;
      recipientAccountNumber: string;
      exchangeActualRate?: string;
      exchangeMarginPercent?: string;
      inverseActualRate?: string;
      quoteId?: string;
    }) => {
      apiAbortController.abortPendingRequest();

      setCalcError('');
      setErrorMessage('');

      const body: CalculateTransactionRateFormValues = {
        exchangeDateOfRate: calculateFields.exchangeDateOfRate,
        exchangeRateSourceId: calculateFields.exchangeRateSourceId,
        exchangeCurrencyFromId: calculateFields.exchangeCurrencyFromId,
        exchangeCurrencyToId: calculateFields.exchangeCurrencyToId,
        exchangeCurrencyFixedId: calculateFields.exchangeCurrencyFixedId,
        sum: Number(calculateFields.sum),
        senderAccountNumber: calculateFields.senderAccountNumber,
        recipientAccountNumber: calculateFields.recipientAccountNumber,
      };

      const nextCalculatedBodyValues: any = {
        exchangeDateOfRate: calculateFields.exchangeDateOfRate,
        exchangeRateSourceId: calculateFields.exchangeRateSourceId,
        exchangeCurrencyFromId: calculateFields.exchangeCurrencyFromId,
        exchangeCurrencyToId: calculateFields.exchangeCurrencyToId,
        exchangeCurrencyFixedId: calculateFields.exchangeCurrencyFixedId,
        sum: calculateFields.sum,
        senderAccountNumber: calculateFields.senderAccountNumber,
        recipientAccountNumber: calculateFields.recipientAccountNumber,
      };

      if (calculateFields?.quoteId) {
        body.quoteId = calculateFields.quoteId;
      }

      if (calculateFields.exchangeActualRate) {
        body.exchangeActualRate = Number(calculateFields.exchangeActualRate);
      }

      if (calculateFields.exchangeMarginPercent) {
        body.exchangeMarginPercent = Number(
          calculateFields.exchangeMarginPercent,
        );
      }

      if (calculateFields.inverseActualRate) {
        body.inverseActualRate = Number(calculateFields.inverseActualRate);
      }

      const response = (await calculate(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data: CalculateTransactionRateResult };
      };

      if (response?.error) {
        setValue('exchangeProfit', undefined);
        setValue('baseRate', undefined);
        setValue('rate', undefined);
        setValue('providersRate', undefined);
        setValue('exchangeProfitRate', undefined);
        setValue('calculatedAmount', undefined);
        setValue('inverseActualRate', undefined);
        setValue('inverseBaseRate', undefined);
        setValue('inverseRate', undefined);

        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCalcError,
        });
      } else {
        clearErrors([
          'sum',
          'exchangeCurrencyFromId',
          'exchangeCurrencyToId',
          'exchangeCurrencyFixedId',
        ]);

        setCalculateOnSwapEnabled(false);

        if (response?.data?.data?.quoteId) {
          nextCalculatedBodyValues.quoteId = response?.data?.data?.quoteId;
        }
      }

      setCalculatedBodyValues(nextCalculatedBodyValues);
    },
    [],
  );

  const { getTransaction, onClearTimer, internalError } = useRetry({
    getTransactionFunction: getExchangeTransactionById,
    id: transactionId as string,
    isRetry: true,
    getStatusesFunction: getStatusesTransactionById,
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_EXCHANGE_EDIT_ACTION,
    ),
    chatPermission: activePermissions?.includes(PermissionEnum.CHANNELS_LIST),
    statusUrl: StatusesUrl.EXCHANGE,
  });

  const calculateOnSwap = ({
    currencyFrom,
    currencyTo,
    currencyFixed,
  }: {
    currencyFrom: string;
    currencyTo: string;
    currencyFixed: string;
  }) => {
    setCalculateOnSwapEnabled(true);

    calculateTransactionRatesAction({
      exchangeDateOfRate: fields.exchangeDateOfRate,
      exchangeRateSourceId: fields.exchangeRateSourceId,
      exchangeCurrencyFromId: currencyFrom,
      exchangeCurrencyToId: currencyTo,
      exchangeCurrencyFixedId: currencyFixed,
      sum: exchangeType === 'SELL' ? fields.sellSum : fields?.buySum,
      senderAccountNumber: fields.senderAccountNumber,
      recipientAccountNumber: fields.recipientAccountNumber,
      quoteId: calculateResult?.quoteId,
    });
  };

  const onUpdateRates = () => {
    if (!transactionId) {
      calculateTransactionRatesAction({
        exchangeDateOfRate: fields.exchangeDateOfRate,
        exchangeRateSourceId: fields.exchangeRateSourceId,
        exchangeCurrencyFromId: fields.exchangeCurrencyFromId,
        exchangeCurrencyToId: fields.exchangeCurrencyToId,
        exchangeCurrencyFixedId:
          exchangeType === 'SELL'
            ? fields.exchangeCurrencyFromId
            : fields.exchangeCurrencyToId,
        sum: exchangeType === 'SELL' ? fields.sellSum : fields?.buySum,
        senderAccountNumber: fields.senderAccountNumber,
        recipientAccountNumber: fields.recipientAccountNumber,
        quoteId: calculateResult?.quoteId,
      });
    } else {
      calculateTransactionRatesByIdAction(
        {
          exchangeDateOfRate: fields.exchangeDateOfRate as string,
          exchangeRateSourceId: fields.exchangeRateSourceId,
          exchangeMarginPercent: fields.exchangeMarginPercent,
          exchangeActualRate: fields.exchangeActualRate,
        },
        'margin',
      );
    }
  };

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeDateOfRate &&
      fields.exchangeRateSourceId &&
      fields.exchangeCurrencyFromId &&
      fields.exchangeCurrencyToId &&
      fields.exchangeCurrencyFixedId &&
      ((fields.sellSum && fields.sellSum !== 0) ||
        (fields.buySum && fields.buySum !== 0)) &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      exchangeType &&
      !calculatedBodyValues
    ) {
      calculateTransactionRatesAction({
        exchangeDateOfRate: fields.exchangeDateOfRate,
        exchangeRateSourceId: fields.exchangeRateSourceId,
        exchangeCurrencyFromId: fields.exchangeCurrencyFromId,
        exchangeCurrencyToId: fields.exchangeCurrencyToId,
        exchangeCurrencyFixedId:
          exchangeType === 'SELL'
            ? fields.exchangeCurrencyFromId
            : fields.exchangeCurrencyToId,
        sum: exchangeType === 'SELL' ? fields.sellSum : fields.buySum,
        senderAccountNumber: fields.senderAccountNumber,
        recipientAccountNumber: fields.recipientAccountNumber,
        quoteId: calculateResult?.quoteId,
      });
    }
  }, [
    calculateTransactionRatesAction,
    fields.exchangeDateOfRate,
    fields.exchangeRateSourceId,
    fields.exchangeCurrencyFromId,
    fields.exchangeCurrencyToId,
    fields.exchangeCurrencyToId,
    fields.sellSum,
    fields.buySum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    transactionId,
    exchangeType,
    calculatedBodyValues,
    calculateResult?.quoteId,
  ]);

  useEffect(() => {
    if (transactionId) {
      setExchangeDateWithTime(true);
      setPostingDateWithTime(true);
      setTransferDateWithTime(true);
    }
  }, [transactionId]);

  useEffect(() => {
    if (
      !transactionId &&
      exchangeType !== prevExchangeType &&
      calculatedBodyValues &&
      !inverseRateChanged
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeCurrencyFixedId:
          exchangeType === 'SELL'
            ? fields.exchangeCurrencyFromId
            : fields.exchangeCurrencyToId,
        sum: exchangeType === 'SELL' ? fields.sellSum : fields.buySum,
      });
    }
  }, [exchangeType, prevExchangeType]);

  useEffect(() => {
    if (
      !transactionId &&
      calculatedBodyValues &&
      ((exchangeType === 'SELL' && fields.sellSum !== prevSellSum) ||
        (exchangeType === 'BUY' && fields.buySum !== prevBuySum))
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        sum: exchangeType === 'SELL' ? fields.sellSum : fields.buySum,
      });
    }
  }, [exchangeType, fields.buySum, fields.sellSum, prevSellSum, prevBuySum]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeCurrencyFromId &&
      fields.exchangeCurrencyFromId !== prevCurrencyFrom &&
      calculatedBodyValues &&
      !calculateOnSwapEnabled
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeCurrencyFromId: fields.exchangeCurrencyFromId,
        exchangeCurrencyFixedId:
          exchangeType === 'SELL'
            ? fields.exchangeCurrencyFromId
            : calculatedBodyValues.exchangeCurrencyFixedId,
      });
    }
  }, [fields.exchangeCurrencyFromId, prevCurrencyFrom, calculateOnSwapEnabled]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeCurrencyToId &&
      fields.exchangeCurrencyToId !== prevCurrencyTo &&
      calculatedBodyValues &&
      !calculateOnSwapEnabled
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeCurrencyToId: fields.exchangeCurrencyToId,
        exchangeCurrencyFixedId:
          exchangeType === 'BUY'
            ? fields.exchangeCurrencyToId
            : calculatedBodyValues.exchangeCurrencyFixedId,
      });
    }
  }, [fields.exchangeCurrencyToId, prevCurrencyTo, calculateOnSwapEnabled]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      calculatedBodyValues
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        senderAccountNumber: fields.senderAccountNumber,
        recipientAccountNumber: fields.recipientAccountNumber,
      });
    }
  }, [fields.senderAccountNumber, prevSender, fields.recipientAccountNumber]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeActualRate &&
      fields.exchangeActualRate !== prevExchangeActualRate &&
      calculatedBodyValues
    ) {
      setInverseRateChanged(false);

      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeActualRate: fields.exchangeActualRate,
      });
    }
  }, [fields.exchangeActualRate, prevExchangeActualRate]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.inverseActualRate &&
      prevInversedRate &&
      Number(fields.inverseActualRate) !== Number(prevInversedRate) &&
      calculatedBodyValues &&
      inverseRateChanged
    ) {
      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        inverseActualRate: fields.inverseActualRate,
      });
    }
  }, [fields.inverseActualRate, prevInversedRate, inverseRateChanged]);

  useEffect(() => {
    if (
      !transactionId &&
      calculatedBodyValues &&
      rateSource &&
      prevRateSource !== rateSource
    ) {
      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeRateSourceId: rateSource,
        exchangeActualRate: fields.exchangeActualRate,
      });
    }
  }, [rateSource, prevRateSource]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeMarginPercent &&
      fields.exchangeMarginPercent !== prevExchangeMarginPercent &&
      calculatedBodyValues
    ) {
      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeMarginPercent: fields.exchangeMarginPercent,
      });
    }
  }, [fields.exchangeMarginPercent, prevExchangeMarginPercent]);

  useEffect(() => {
    if (
      !transactionId &&
      fields.exchangeDateOfRate &&
      prevExchangeDateOfRate &&
      fields.exchangeDateOfRate.slice(0, 10) !==
        prevExchangeDateOfRate.slice(0, 10) &&
      calculatedBodyValues
    ) {
      calculateTransactionRatesAction({
        ...calculatedBodyValues,
        exchangeDateOfRate: fields.exchangeDateOfRate,
      });
    }
  }, [fields.exchangeDateOfRate, prevExchangeDateOfRate]);

  useEffect(() => {
    if (
      transactionId &&
      fields.exchangeDateOfRate &&
      defaultCalculateResult?.exchangeDateOfRate &&
      fields.exchangeDateOfRate.slice(0, 10) !==
        defaultCalculateResult?.exchangeDateOfRate.slice(0, 10) &&
      transaction?.baseellaStatus !== BaseellaStatus.ARCH &&
      transaction?.baseellaStatus !== BaseellaStatus.PROC &&
      transaction?.baseellaStatus !== BaseellaStatus.AML_REJ &&
      transaction?.baseellaStatus !== BaseellaStatus.REJECTED
    ) {
      calculateTransactionRatesByIdAction(
        {
          exchangeDateOfRate: fields.exchangeDateOfRate,
          exchangeRateSourceId: fields.exchangeRateSourceId,
          exchangeMarginPercent: fields.exchangeMarginPercent,
          exchangeActualRate: fields.exchangeActualRate,
        },
        'date',
      );
    }
  }, [fields.exchangeDateOfRate, defaultCalculateResult, transactionId]);

  useEffect(() => {
    if (calculateResult) {
      setValue('calculatedAmount', calculateResult.calculatedAmount);
      setValue('exchangeProfit', calculateResult.profit);
      setValue(
        'baseRateToCurrencyFrom',
        calculateResult.baseRateToCurrencyFrom,
      );
      setValue('baseRate', calculateResult.baseRate);
      setValue('rate', calculateResult.rate);
      setValue('exchangeCurrencyFixedSum', calculateResult.fixedAmount);
      setValue('baseRateToCurrencyTo', calculateResult.baseRateToCurrencyTo);
      setValue('providersRate', calculateResult.rate);
      setValue('exchangeProfitRate', calculateResult.profitRate);
      setValue('inverseActualRate', calculateResult.inverseActualRate);
      setValue('inverseBaseRate', calculateResult.inverseBaseRate);
      setValue('inverseRate', calculateResult.inverseRate);

      setSubmitDisabled(false);
    }
  }, [calculateResult]);

  return {
    submitDisabled,
    setSubmitDisabled,
    calcError,
    setCalcError,
    isLoading,
    isUninitialized,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    create,
    createSuccess,
    createInProgress,
    editInProgress,
    editIsError,
    calculateInProgress,
    calculateByIdInProgress,
    onSubmit,
    getTransaction,
    onClearTimer,
    internalError,
    onUpdateRates,
    getTransactionsList,
    getStatusesTransactionById,
    calculateTransactionRatesAction,
    calculateTransactionRatesByIdAction,
    calculateOnSwap,
    getSyncedTransactionsAction,
    syncedTransactionsIsLoading,
    syncedTransactionsIsFetching,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    postingDateWithTime,
    exchangeDateWithTime,
    setTransferDateWithTime,
    setPostingDateWithTime,
    setExchangeDateWithTime,
  };
};

export default useRequests;
